import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
interface FormData {
  email: string;
  type: 'user' | 'merchant' | 'enterprise';
  ageConfirm: boolean;
}

const ManifestoPage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  
   const [formData, setFormData] = useState<FormData>({
        email: '',
        type: 'user',
        ageConfirm: false
      });
    const [isSubmitting, setIsSubmitting] = useState(false);
      const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    document.title = 'The Silent Manifesto — Trust in Loyalty | Rewardly Protocol';

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('article:')) {
          el.setAttribute('property', name);
        } else {
          el.setAttribute('name', name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Loyalty is sacred. Not gamified, not manipulated. The dharma-aligned manifesto behind Rewardly Protocol — restoring trust to human value exchange.');
    setMeta('og:title', 'The Silent Manifesto — Rewardly Protocol');
    setMeta('og:description', 'Not just a platform. A prayer. In protocol. The philosophy behind earn-time loyalty rewards.');
    setMeta('og:type', 'website');
    setMeta('og:url', 'https://rewardly.live/manifesto');
    setMeta('og:image', 'https://rewardly.live/og-image.png');
    setMeta('og:site_name', 'Rewardly Protocol');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', '@Rewardly_io');
    setMeta('twitter:title', 'The Silent Manifesto — Rewardly Protocol');
    setMeta('twitter:description', 'Not just a platform. A prayer. In protocol. The philosophy behind earn-time loyalty rewards.');
    setMeta('twitter:image', 'https://rewardly.live/og-image.png');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://rewardly.live/manifesto');
  }, []);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalOpen]);

  const validateEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };
      const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        
        // Validate email
        if (!validateEmail(formData.email)) {
          setSubmitMessage('Please enter a valid email address');
          return;
        }
    
        // Validate age confirmation
        if (!formData.ageConfirm) {
          setSubmitMessage('Please confirm you are 18 or older');
          return;
        }
        setIsSubmitting(true);
        setSubmitMessage('');
        try {
          const response = await fetch('https://5g72iuzmj9.execute-api.us-east-1.amazonaws.com/dev/save', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              email: formData.email,
              user_type: formData.type
            })
          });
    
          const raw = await response.json();
          let data: any;
          if (raw.body && typeof raw.body === 'string') {
            try { data = JSON.parse(raw.body); } catch { data = raw; }
          } else { data = raw; }
          const statusCode = raw.statusCode || response.status;
          if (statusCode === 200 && !data.error) {
            if (data.new_subscription) {
              setSubmitMessage('Welcome! You\'re on the list. Check your email for confirmation. Follow @Rewardly_io for updates.');
            } else {
              setSubmitMessage('You\'re already on the waitlist! We\'ll email you when early access opens.');
            }
            setFormData({ email: '', type: 'user', ageConfirm: false });
            setTimeout(() => {
              setModalOpen(false);
              setSubmitMessage('');
            }, 3000);
          } else {
            setSubmitMessage(data.error || 'Failed to join waitlist. Please try again.');
          }
        } catch (error) {
          console.error('Error submitting email:', error);
          setSubmitMessage('An error occurred. Please try again.');
        } finally {
          setIsSubmitting(false);
        }
      };
    
      const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
        const target = e.target as HTMLInputElement;
        const { name, value, type, checked } = target;
        setFormData(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error message when user starts typing
        if (submitMessage) setSubmitMessage('');
      };
  

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=Outfit:wght@400;500;600;700;800&display=swap');
        
        :root {
          --white: #FFFFFF;
          --off-white: #F8FAFC;
          --gray-100: #F1F5F9;
          --gray-200: #E2E8F0;
          --gray-400: #94A3B8;
          --gray-500: #64748B;
          --gray-900: #0F172A;
          --blue-600: #2563EB;
          --blue-700: #1D4ED8;
          --gold: #D4A853;
          --gold-dark: #B8923A;
          --green-600: #059669;
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        
        body {
          font-family: 'Inter', -apple-system, sans-serif;
          background: var(--white);
          color: var(--gray-900);
          line-height: 1.6;
          -webkit-font-smoothing: antialiased;
        }

        h1, h2, h3, .section-title, .stat-value, .inversion-title {
          font-family: 'Outfit', 'Inter', -apple-system, sans-serif;
        }

        .container {
          max-width: 1140px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .container-narrow {
          max-width: 720px;
          margin: 0 auto;
          padding: 0 24px;
        }

        nav {
          position: fixed;
          top: 0;
          width: 100%;
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--gray-200);
          z-index: 1000;
        }

        .nav-inner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: 72px;
        }

        .nav-menu {
          display: flex;
          align-items: center;
          gap: 32px;
        }

        .nav-menu > a, .dropdown > a {
          color: var(--gray-900);
          text-decoration: none;
          font-weight: 500;
          font-size: 15px;
          transition: color 0.2s;
          cursor: pointer;
        }

        .nav-menu > a:hover, .dropdown > a:hover { color: var(--blue-600); }

        .dropdown {
          position: relative;
        }

        .dropdown-toggle {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .dropdown-toggle::after {
          content: '▼';
          font-size: 10px;
          transition: transform 0.2s;
        }

        .dropdown:hover .dropdown-toggle::after {
          transform: rotate(180deg);
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          padding: 8px 0;
          min-width: 180px;
          margin-top: 8px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all 0.2s;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
        }

        .dropdown:hover .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-menu a {
          display: block;
          padding: 10px 20px;
          color: var(--gray-900);
          text-decoration: none;
          font-size: 14px;
          transition: background 0.2s;
        }

        .dropdown-menu a:hover {
          background: var(--gray-100);
        }

        .dropdown.nested {
          position: relative;
        }

        .dropdown.nested > a {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          cursor: pointer;
        }

        .dropdown.nested > a::after {
          content: '→';
          font-size: 12px;
          margin-left: auto;
          transition: transform 0.2s;
        }

        .dropdown.nested:hover > a::after {
          transform: translateX(2px);
        }

        .dropdown > .dropdown-menu > .dropdown.nested > .dropdown-menu {
          position: absolute;
          top: 0;
          left: 100%;
          margin-top: 0;
          margin-left: 8px;
          opacity: 0;
          visibility: hidden;
          transform: translateX(-10px);
          transition: all 0.2s;
          pointer-events: none;
        }

        .dropdown > .dropdown-menu > .dropdown.nested:hover > .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateX(0);
          pointer-events: auto;
        }

        .nav-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          transition: all 0.2s;
          border: none;
          cursor: pointer;
        }

        .btn-primary { background: var(--blue-600); color: var(--white); }
        .btn-primary:hover { background: var(--blue-700); }
        .btn-outline { background: transparent; color: var(--gray-900); border: 1px solid var(--gray-200); }
        .btn-outline:hover { border-color: var(--blue-600); color: var(--blue-600); }
        .btn-gold { background: var(--gold); color: var(--gray-900); }
        .btn-gold:hover { background: var(--gold-dark); }
        .btn-secondary { background: var(--white); color: var(--gray-900); border: 1px solid var(--gray-200); }
        .btn-secondary:hover { background: var(--gray-100); }

        .mobile-toggle {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
        }
        .mobile-toggle span { width: 22px; height: 2px; background: var(--gray-900); }

        .mobile-menu {
          display: none;
          position: fixed;
          top: 72px;
          left: 0;
          right: 0;
          background: var(--white);
          border-bottom: 1px solid var(--gray-200);
          padding: 16px 24px;
          z-index: 999;
        }
        .mobile-menu.active { display: block; }
        .mobile-menu a {
          display: block;
          padding: 12px 0;
          color: var(--gray-900);
          text-decoration: none;
          font-weight: 500;
          border-bottom: 1px solid var(--gray-100);
        }
        .mobile-menu .mobile-section {
          font-size: 12px;
          color: var(--gray-500);
          text-transform: uppercase;
          margin-top: 16px;
          margin-bottom: 8px;
        }

        .modal-overlay {
          display: none;
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 2000;
          align-items: center;
          justify-content: center;
        }
        .modal-overlay.active { display: flex; }
        .modal {
          background: var(--white);
          border-radius: 16px;
          padding: 32px;
          width: 100%;
          max-width: 440px;
          margin: 24px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
        }
        .modal h3 { font-size: 24px; font-weight: 700; margin-bottom: 24px; }
        .modal-field { margin-bottom: 20px; }
        .modal-field label { display: block; font-size: 14px; font-weight: 500; color: var(--gray-500); margin-bottom: 8px; }
        .modal-field input, .modal-field select {
          width: 100%; padding: 14px 16px; border: 1px solid var(--gray-200);
          border-radius: 8px; font-size: 15px; font-family: inherit;
        }
        .modal-field input:focus, .modal-field select:focus { outline: none; border-color: var(--blue-600); }
        .modal-checkbox { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 24px; }
        .modal-checkbox input { width: 18px; height: 18px; margin-top: 2px; accent-color: var(--blue-600); }
        .modal-checkbox span { font-size: 14px; color: var(--gray-500); line-height: 1.5; }
        .modal-buttons { display: flex; gap: 12px; }
        .modal-buttons .btn { flex: 1; justify-content: center; padding: 14px 20px; }

        .hero {
          padding: 140px 0 80px;
          background: var(--gray-900);
          text-align: center;
        }

        .hero-label {
          display: inline-block;
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2);
          padding: 8px 20px;
          border-radius: 30px;
          font-size: 13px;
          font-weight: 600;
          color: rgba(255,255,255,0.8);
          margin-bottom: 24px;
        }

        .hero h1 {
          font-size: clamp(36px, 5vw, 52px);
          font-weight: 800;
          line-height: 1.1;
          color: var(--white);
          margin-bottom: 24px;
          letter-spacing: -0.02em;
        }

        .hero h1 .gold { color: var(--gold); }

        .hero-quote {
          font-family: 'Crimson Text', serif;
          font-size: 22px;
          font-style: italic;
          color: rgba(255,255,255,0.7);
          max-width: 560px;
          margin: 0 auto;
          line-height: 1.6;
          letter-spacing: 0.3px;
        }

        .intro-section {
          padding: 80px 0;
          text-align: center;
          border-bottom: 1px solid var(--gray-200);
        }

        .intro-text {
          font-family: 'Crimson Text', serif;
          font-size: clamp(20px, 3vw, 26px);
          font-style: italic;
          color: var(--gray-900);
          line-height: 1.7;
          max-width: 640px;
          margin: 0 auto;
        }

        .beliefs-section {
          padding: 80px 0;
        }

        .belief-item {
          padding: 40px 0;
          border-bottom: 1px solid var(--gray-200);
        }

        .belief-item:last-child { border-bottom: none; }

        .belief-text {
          font-family: 'Crimson Text', serif;
          font-size: clamp(18px, 2.5vw, 22px);
          color: var(--gray-900);
          line-height: 1.7;
          margin-bottom: 16px;
        }

        .belief-text strong {
          color: var(--green-600);
          font-weight: 600;
        }

        .belief-expansion {
          font-size: 15px;
          color: var(--gray-500);
          line-height: 1.7;
          padding-left: 20px;
          border-left: 3px solid var(--gold);
          max-width: 600px;
        }

        .closing-section {
          padding: 80px 0;
          background: var(--off-white);
          text-align: center;
        }

        .closing-text {
          font-family: 'Crimson Text', serif;
          font-size: clamp(20px, 3vw, 26px);
          font-style: italic;
          color: var(--gray-900);
          line-height: 1.6;
          margin-bottom: 24px;
        }

        .closing-signature {
          font-size: 24px;
          font-weight: 800;
          color: var(--gray-900);
        }

        .prayer-section {
          padding: 80px 0;
          background: var(--gray-900);
          text-align: center;
        }

        .prayer-box {
          max-width: 560px;
          margin: 0 auto;
          padding: 48px 40px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 16px;
        }

        .prayer-text {
          font-family: 'Crimson Text', serif;
          font-size: clamp(18px, 2.5vw, 22px);
          font-style: italic;
          color: var(--white);
          line-height: 1.7;
          margin-bottom: 24px;
        }

        .prayer-attribution {
          color: var(--gold);
          font-size: 14px;
          font-weight: 600;
        }

        .links-section {
          padding: 64px 0;
          text-align: center;
        }

        .links-title {
          font-size: 18px;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 24px;
        }

        .links-grid {
          display: flex;
          justify-content: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .link-card {
          padding: 16px 28px;
          background: var(--off-white);
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          text-decoration: none;
          color: var(--gray-900);
          font-weight: 600;
          font-size: 14px;
          transition: all 0.2s;
        }

        .link-card:hover {
          background: var(--blue-600);
          color: var(--white);
          border-color: var(--blue-600);
        }

        .cta-section {
          padding: 80px 0;
        background: linear-gradient(135deg, var(--blue-600) 0%, var(--blue-700) 100%);
          text-align: center;
        }

        .cta-section h2 {
          font-size: clamp(28px, 4vw, 36px);
          font-weight: 800;
          color: var(--white);
          margin-bottom: 12px;
        }

        .cta-section p {
          color: rgba(255,255,255,0.7);
          font-size: 17px;
          margin-bottom: 32px;
        }

        footer {
          background: var(--gray-900);
          color: var(--white);
          padding: 60px 0 40px;
        }

        .footer-content {
          display: grid;
          grid-template-columns: 1fr 2fr 1fr;
          gap: 40px;
          margin-bottom: 40px;
        }

        .footer-brand h3 {
          font-size: 24px;
          margin-bottom: 12px;
        }

        .footer-brand p {
          color: rgba(255,255,255,0.6);
          font-size: 14px;
          line-height: 1.6;
        }

        .footer-links h4 {
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 20px;
          color: rgba(255,255,255,0.5);
        }

        .footer-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .footer-grid a {
          color: rgba(255,255,255,0.8);
          text-decoration: none;
          font-size: 14px;
          transition: color 0.2s;
        }

        .footer-grid a:hover {
          color: var(--white);
        }

        .footer-social h4 {
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 20px;
          color: rgba(255,255,255,0.5);
        }

        .social-links {
          display: flex;
          gap: 12px;
        }

        .social-links a {
          width: 40px;
          height: 40px;
          background: rgba(255,255,255,0.1);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255,255,255,0.6);
          transition: all 0.2s;
        }

        .social-links a:hover {
          background: rgba(255,255,255,0.2);
          color: var(--white);
        }

        .footer-bottom {
          padding-top: 40px;
          border-top: 1px solid rgba(255,255,255,0.1);
          text-align: center;
          color: rgba(255,255,255,0.5);
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .nav-menu, .nav-actions { display: none; }
          .mobile-toggle { display: flex; }

          .hero { padding: 100px 0 48px; }
          .hero h1 { font-size: 32px; }

          .beliefs-section { padding: 48px 0; }
          .belief-item { padding: 24px 0; }

          .closing-section { padding: 48px 0; }

          .links-section { padding: 40px 0; }
          .links-grid { flex-direction: column; align-items: center; }
          .link-card { width: 100%; text-align: center; box-sizing: border-box; }

          .cta-section { padding: 48px 0; }

          .footer-content { grid-template-columns: 1fr; }
          .footer-grid { grid-template-columns: repeat(2, 1fr); }
          .modal-buttons { flex-direction: column; }
        }
      `}</style>

        {/* Modal */}
     <div 
        className={`modal-overlay ${modalOpen ? 'active' : ''}`}
        onClick={() => setModalOpen(false)}
      >
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h3>Join the Waitlist</h3>
          
          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('Thank you') ? 'success' : 'error'}`}>
              {submitMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="modal-field">
              <input 
                type="email" 
                name="email"
                placeholder="Enter your email" 
                value={formData.email}
                onChange={handleInputChange}
                required 
                disabled={isSubmitting}
              />
            </div>
            <div className="modal-field">
              <label>I am a...</label>
              <select 
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              >
                <option value="user">User</option>
                <option value="merchant">Merchant</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="modal-checkbox">
              <input 
                type="checkbox" 
                name="ageConfirm"
                id="ageConfirm" 
                checked={formData.ageConfirm}
                onChange={handleInputChange}
                required 
                disabled={isSubmitting}
              />
              <span>I am 18 or older and agree to the <a target='_blank' rel='noopener noreferrer' href="/terms">Terms of Service and Privacy Policy</a></span>
            </div>
            <div className="modal-buttons">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
              </button>
              <button 
                type="button"
                onClick={() => setModalOpen(false)} 
                className="btn btn-outline"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

    <Header onJoinWaitlist={() => setModalOpen(true)} />

      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero-label">A Dharma-Aligned Manifesto</div>
          <h1>The Silent <span className="gold">Manifesto</span></h1>
          <p className="hero-quote">Loyalty is sacred. Not mined, not manipulated. Built on dharma — designed for trust.</p>
        </div>
      </section>

      {/* Intro */}
      <section className="intro-section">
        <div className="container-narrow">
          <p className="intro-text">
            We believe loyalty is sacred. Not gamified. Not mined. Not manipulated.
            Just remembered. Honored. Circulated.
          </p>
        </div>
      </section>

      {/* Beliefs */}
      <section className="beliefs-section">
        <div className="container-narrow">
          <div className="belief-item">
            <p className="belief-text">
              <strong>Loyalty is gratitude made visible.</strong> Every organization — whether a mall, a hospital, a temple, or a government — has someone to thank.
            </p>
            <p className="belief-expansion">
              A customer who returned. A patient who followed their care plan. A citizen who rode transit instead of driving. A donor who gave without being asked. Rewardly Protocol makes that gratitude tangible, permanent, and transferable.
            </p>
          </div>

          <div className="belief-item">
            <p className="belief-text">
              <strong>Rewards are energy.</strong> They carry memory. They reflect trust.
              They should flow freely across borders, brands, and beings.
            </p>
            <p className="belief-expansion">
              A point is not just a credit — it's a receipt of trust. A redemption is not just a transaction — it's a ritual of completion. When value stagnates, it loses its essence. Rewardly Protocol enables circulation — transforming locked value into living currency.
            </p>
          </div>

          <div className="belief-item">
            <p className="belief-text">
              <strong>Circulation, not accumulation.</strong> Value multiplies when it moves.
              Loyalty decays when it is locked.
            </p>
            <p className="belief-expansion">
              Hoarded value doesn't multiply — circulated value does. Systems that begin with trust are lighter, faster, and more magnetic. Our tokenomics reward movement, not stasis. Regional multipliers ensure equitable access.
            </p>
          </div>

          <div className="belief-item">
            <p className="belief-text">
              <strong>Trust is the real asset.</strong> Not metrics. Not margins. Not manipulation.
              When systems honor trust, loyalty becomes alive.
            </p>
            <p className="belief-expansion">
              The most powerful economy is one where trust circulates freely — and multiplies. We measure success by the integrity of every exchange. Every fee is transparent. Every rate is visible. Every promise is kept.
            </p>
          </div>

          <div className="belief-item">
            <p className="belief-text">
              <strong>Transparency as default.</strong> Every token, every fee, every reward—
              clear, visible, verifiable.
            </p>
            <p className="belief-expansion">
              Architecture isn't just code — it's intention written into form. All contracts are published and auditable. All roles are visible. All governance is accessible. Nothing hidden. Nothing excessive.
            </p>
          </div>

          <div className="belief-item">
            <p className="belief-text">
              <strong>Enterprises as stewards.</strong> Their job is not extraction—
              it is long-term value creation.
            </p>
            <p className="belief-expansion">
              Enterprises are not platforms to monetize — they are custodians of trust. We partner with those who see customers as people, not data points. Who build for legacy, not liquidity events.
            </p>
          </div>

        </div>
      </section>

      {/* The Questions We Ask */}
      <section style={{padding: '64px 0', background: 'var(--off-white, #F8F7F4)'}}>
        <div className="container-narrow" style={{maxWidth: '680px', margin: '0 auto', padding: '0 24px'}}>
          <div style={{background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '12px', padding: '40px'}}>
            <p style={{fontSize: '15px', color: 'var(--gray-600)', marginBottom: '20px', lineHeight: 1.7}}>Before every feature ships, before every contract deploys, we ask:</p>
            <p style={{fontSize: '17px', fontStyle: 'italic', color: 'var(--gray-900)', marginBottom: '12px', lineHeight: 1.7}}><em>Does this honor the trust they gave us?</em></p>
            <p style={{fontSize: '17px', fontStyle: 'italic', color: 'var(--gray-900)', marginBottom: '12px', lineHeight: 1.7}}><em>Are we creating circuits — or cages?</em></p>
            <p style={{fontSize: '17px', fontStyle: 'italic', color: 'var(--gray-900)', marginBottom: '12px', lineHeight: 1.7}}><em>Are we circulating value — or accumulating data?</em></p>
            <p style={{fontSize: '17px', fontStyle: 'italic', color: 'var(--gray-900)', marginBottom: '24px', lineHeight: 1.7}}><em>Does this system reward alignment — or punish transparency?</em></p>
            <p style={{fontSize: '14px', color: 'var(--gray-500)', lineHeight: 1.7}}>These aren't slogans. They're the engineering review criteria embedded in every pull request.</p>
          </div>
        </div>
      </section>

      {/* Our Commitments */}
      <section style={{padding: '64px 0', background: 'var(--off-white, #F8F7F4)'}}>
        <div className="container-narrow" style={{maxWidth: '720px', margin: '0 auto', padding: '0 24px'}}>
          <div style={{background: '#fff', borderLeft: '4px solid var(--gold, #D4A853)', borderRadius: '0 12px 12px 0', padding: '36px 40px'}}>
            <h3 style={{fontSize: '18px', fontWeight: 700, marginBottom: '24px', color: 'var(--gray-900)'}}>Our Commitments</h3>
            <ol style={{paddingLeft: '20px', margin: 0}}>
              {[
                'All smart contracts will complete independent third-party security audit before mainnet deployment.',
                'No hidden minting capability — total supply capped at 120B, verifiable on-chain, enforced by code.',
                'Progressive decentralization: guided DAO governance activates by month 13, full community governance by month 25.',
                'No loyalty points will expire in the Rewardly Protocol network. Ever.',
                'Reward value is guaranteed at earn time — locked by oracle price, immutable by design. Patent pending.'
              ].map((commitment, i) => (
                <li key={i} style={{fontSize: '15px', lineHeight: 1.7, color: 'var(--gray-700)', marginBottom: i < 4 ? '16px' : 0}}>{commitment}</li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="closing-section">
        <div className="container-narrow">
          <p style={{fontSize: 'clamp(20px,3vw,28px)', fontWeight: 700, textAlign: 'center', color: 'var(--gray-900)', marginBottom: '8px', lineHeight: 1.4}}>The world doesn't need another app.</p>
          <p style={{fontSize: 'clamp(20px,3vw,28px)', fontWeight: 700, textAlign: 'center', color: 'var(--gray-900)', marginBottom: '40px', lineHeight: 1.4}}>It needs architecture for trust.</p>
          <hr style={{border: 'none', borderTop: '1px solid var(--gray-200)', marginBottom: '40px'}} />
          <p className="closing-text">
            Not just a platform.<br />
            A prayer. In protocol.
          </p>
          <div className="closing-signature">Rewardly Protocol</div>
        </div>
      </section>

      {/* Links */}
      <section className="links-section">
        <div className="container">
          <h3 className="links-title">Continue the Journey</h3>
          <div className="links-grid">
            <a href="/vision" className="link-card">Read Our Vision</a>
            <a href="/whitepaper" className="link-card">Technical Whitepaper</a>
            <a href="/trust-framework" className="link-card">Trust Framework</a>
            <a href="/innovation" className="link-card">Innovation &amp; IP</a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2>Join the Movement</h2>
          <p>Be part of restoring trust to commerce.</p>
          <div style={{display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap'}}>
            <button onClick={() => setModalOpen(true)} className="btn btn-gold" style={{padding: '16px 32px', fontSize: '16px'}}>Join the Movement →</button>
            <a href="/trust-framework" style={{display: 'inline-flex', alignItems: 'center', padding: '16px 32px', fontSize: '16px', borderRadius: '8px', fontWeight: 600, textDecoration: 'none', border: '1px solid rgba(255,255,255,0.3)', color: '#FFFFFF', transition: 'all 0.2s'}}>Read the Trust Framework →</a>
          </div>
        </div>
      </section>

      {/* Footer */}
 <Footer />
    </div>
  );
};

export default ManifestoPage;